import './TransitionExpand.sass.js';
import { defineComponent, computed, h } from 'vue';

const dimensions = {
  height: {
    opposite: 'width',
    clientProperty: 'clientHeight',
    marginProperty: 'marginTop'
  },
  width: {
    opposite: 'height',
    clientProperty: 'clientWidth',
    marginProperty: 'marginLeft'
  }
};
var TransitionExpand = defineComponent({
  name: 'TransitionExpand',
  props: {
    element: {
      type: [String, Object],
      default: 'div'
    },
    fade: Boolean,
    fast: Boolean,
    group: Boolean,
    horizontal: Boolean,
    groupElement: {
      type: [String, Object],
      default: 'div'
    },
    mode: {
      type: String,
      default: 'out-in'
    },
    spaceBetween: {
      type: Number,
      default: null
    }
  },

  setup(props, {
    slots
  }) {
    const dimension = props.horizontal ? 'width' : 'height';
    const oppositeDimension = dimensions[dimension].opposite;

    const measureElement = element => {
      const oppositeDimensionValue = getComputedStyle(element)[oppositeDimension];
      element.style.setProperty(oppositeDimension, oppositeDimensionValue);
      element.style.setProperty('position', 'absolute');
      element.style.setProperty('visibility', 'hidden');
      const measurement = getComputedStyle(element)[dimension];
      element.style.removeProperty(oppositeDimension);
      element.style.removeProperty('position');
      element.style.removeProperty('visibility');
      return measurement;
    };

    const enter = element => {
      const measurement = measureElement(element);
      element.style.setProperty(dimension, '0px');
      requestAnimationFrame(() => {
        element.style.setProperty(dimension, measurement);
      });
    };

    const beforeLeave = element => {
      const clientDimension = dimensions[dimension].clientProperty;
      element.style.setProperty(dimension, `${element[clientDimension]}px`);
      requestAnimationFrame(() => {
        element.style.setProperty(dimension, '0px');
      });
    };

    const removeExplicitDimension = element => {
      element.style.removeProperty(dimension);
    };

    const rootClassNames = computed(() => {
      const names = ['TransitionExpand'];

      if (props.fade) {
        names.push('TransitionExpand--fade');
      }

      if (props.fast) {
        names.push('TransitionExpand--fast');
      }

      return names;
    });
    const classNames = computed(() => {
      // There is no root element on which to apply class names in the
      // single `<transition>` element.
      if (!props.group) return [];
      const classes = [...rootClassNames.value, 'TransitionExpand--group'];

      if (props.mode === 'bounce') {
        classes.push('TransitionExpand--cs-bounce');
      }

      return classes;
    });
    const childClassNames = computed(() => {
      if (!props.group) {
        return rootClassNames.value;
      }

      const classes = ['TransitionExpand__group-element'];

      if (props.mode === 'bounce') {
        classes.push('TransitionExpand--cs-bounce');
      }

      return classes;
    });
    return () => {
      const addMarginToChild = child => {
        var _a;

        let existingStyle = (_a = child.data) === null || _a === void 0 ? void 0 : _a.style;
        if (!existingStyle || typeof existingStyle === 'string' || Array.isArray(existingStyle)) existingStyle = {};
        return { ...child,
          data: { ...child.data,
            style: { ...existingStyle,
              [dimensions[dimension].marginProperty]: `${props.spaceBetween}px`
            }
          }
        };
      };

      const wrapChildWithTag = (child, tag) => h(tag, {
        class: childClassNames.value,
        // VNodeData has more strict types for key than VNode. We can b
        // confident that key will be a string or number.
        key: child.key
      }, [child]);

      const wrapChildrenWithTransition = children => h(props.group ? 'transition-group' : 'transition', {
        class: classNames.value,
        props: {
          name: 'expand',
          mode: props.mode,
          tag: props.group ? props.element : null
        },
        on: {
          enter,
          afterEnter: removeExplicitDimension,
          beforeLeave,
          afterLeave: removeExplicitDimension
        }
      }, children);

      const getContent = () => {
        var _a, _b;

        const elementForSlotNodes = props.group ? props.groupElement : props.element;
        const slotNodes = (_a = slots === null || slots === void 0 ? void 0 : slots.default) === null || _a === void 0 ? void 0 : _a.call(slots); // Text-only nodes are discarded.

        const wrappedSlotNodes = (_b = slotNodes === null || slotNodes === void 0 ? void 0 : slotNodes.filter(node => !!node.tag).map((node, index) => {
          const addMargin = props.spaceBetween && index !== 0;
          const child = addMargin ? addMarginToChild(node) : node;
          return wrapChildWithTag(child, elementForSlotNodes);
        })) !== null && _b !== void 0 ? _b : [];
        return wrapChildrenWithTransition(wrappedSlotNodes);
      };

      return getContent();
    };
  }

});

export { TransitionExpand as default };
